import React, { useEffect, useState } from 'react'

import ReactDOM from 'react-dom'
import styled from 'styled-components'
import classNames from 'classnames'
import { Button, CloseOutlined, Space } from 'shared/ui'
import { IFsNodeSchema } from '@cloudike/web_files'
import { useTranslation } from 'react-i18next'

export enum RecentlyDeletedToolbarAction {
  Recover = 'recover',
  Delete = 'delete',
}

interface Props {
  selectedItems: IFsNodeSchema[]
  actionsIcons: Record<string, React.ReactNode>
  onActionClick: (actionId: string, selectedItems: IFsNodeSchema[]) => void
  onClose: () => void
  toolbarRef: React.RefObject<HTMLDivElement>
}

export const RecentlyDeletedToolbarTop = ({ selectedItems, actionsIcons, onActionClick, onClose, toolbarRef }: Props) => {
  const { t } = useTranslation()
  const [mounted, setMounted] = useState(false)
  const hasSelection = selectedItems.length > 0

  const actions = [
    { id: RecentlyDeletedToolbarAction.Recover, name: 'Recover' },
    { id: RecentlyDeletedToolbarAction.Delete, name: 'Delete forever' },
  ]

  useEffect(() => {
    setMounted(true)
    return () => setMounted(false)
  }, [])

  const handleAction = (actionId: string) => {
    onActionClick(actionId, selectedItems)
  }

  const itemLabel = selectedItems.length === 1 ? 'item' : 'items'

  const content = (
    <SToolbarContainer ref={toolbarRef}
      className={classNames({ visible: hasSelection })}
    >
      <SToolbarContent>
        <SSelectedInfo>
          <Space>
            <SSelectedCount>
              {selectedItems.length}
            </SSelectedCount>

            {itemLabel}

          </Space>
        </SSelectedInfo>

        <SToolbarActions>
          {actions.map(action => (
            <SActionButton
              key={action.id}
              variant="text"
              color="default"
              icon={actionsIcons[action.id]}
              onClick={() => handleAction(action.id)}
              style={{ color: 'white', fontSize: 15 }}
            >
              {action.name}
            </SActionButton>
          ))}
        </SToolbarActions>

        <SCloseButton
          variant="text"
          color="default"
          icon={<CloseOutlined />}
          onClick={onClose}
          style={{ color: 'white', fontSize: 20 }}
        />
      </SToolbarContent>
    </SToolbarContainer>
  )

  return mounted ? ReactDOM.createPortal(content, document.body) : null
}

const SToolbarContainer = styled.div`
  position: fixed;
  top: 8px;
  left: 50%;
  transform: translateX(-50%) translateY(calc(-100% - 8px));
  transition: transform 0.3s ease;
  z-index: 1000;
  max-width: 1250px;
  width: 100%;
  color: white;

  &.visible {
    transform: translateX(-50%) translateY(0);
  }
`

const SToolbarContent = styled.div`
  display: grid;
  grid-template-columns: 355px 1fr 20px;
  max-width: 1200px;
  margin: 0 auto;
  background-color: #222;
  border-radius: 8px;
  padding: 12px 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`

const SSelectedInfo = styled.div`
  font-size: 15px;
  font-weight: 400;
  display: flex;
  align-items: center;
`

const SSelectedCount = styled.span`
  font-weight: 600;
`

const SToolbarActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  align-items: center;
`

const SActionButton = styled(Button)`
  && {
    &[ant-click-animating-without-extra-node]:after {
      animation: none !important;
    }
  }
`

const SCloseButton = styled(Button)`
  && {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`
